import React, { memo, useCallback, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Tooltip } from '@mui/material';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { message } from 'antd';
import './MarkdownRenderer.css';

/**
 * CodeBlock component for syntax-highlighted code
 */
const CodeBlock = memo(({ language, children, isDarkMode }) => {
    const [copied, setCopied] = useState(false);
    const code = String(children).replace(/\n$/, '');

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            message.success('Copied to clipboard');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            message.error('Failed to copy');
        }
    }, [code]);

    // Detect language from common patterns if not specified
    const detectLanguage = (lang) => {
        if (lang) return lang;
        // Simple detection based on content
        if (code.includes('function') || code.includes('const ') || code.includes('let ')) return 'javascript';
        if (code.includes('def ') || (code.includes('import ') && code.includes(':'))) return 'python';
        if (code.includes('public class') || code.includes('private void')) return 'java';
        if (code.includes('SELECT') || code.includes('FROM') || code.includes('WHERE')) return 'sql';
        return 'text';
    };

    const displayLanguage = detectLanguage(language);

    return (
        <div className="ntb-code-block">
            <div className="ntb-code-header">
                <span className="ntb-code-language">{displayLanguage}</span>
                <Tooltip title={copied ? "Copied!" : "Copy code"} arrow placement="top">
                    <button
                        className={`ntb-code-copy-btn ${copied ? 'copied' : ''}`}
                        onClick={handleCopy}
                        aria-label="Copy code"
                    >
                        {copied ? <CheckOutlined /> : <CopyOutlined />}
                    </button>
                </Tooltip>
            </div>
            <SyntaxHighlighter
                language={displayLanguage}
                style={isDarkMode ? oneDark : oneLight}
                customStyle={{
                    margin: 0,
                    padding: '12px 16px',
                    borderRadius: '0 0 8px 8px',
                    fontSize: '13px',
                    lineHeight: '1.5'
                }}
                showLineNumbers={code.split('\n').length > 3}
                wrapLines
            >
                {code}
            </SyntaxHighlighter>
        </div>
    );
});

CodeBlock.displayName = 'CodeBlock';

/**
 * InlineCode component for inline code snippets
 */
const InlineCode = memo(({ children }) => (
    <code className="ntb-inline-code">{children}</code>
));

InlineCode.displayName = 'InlineCode';

/**
 * MarkdownRenderer component for rendering markdown content
 * Replaces the regex-based formatMarkdown function with react-markdown
 */
const MarkdownRenderer = memo(({ content, isDarkMode = false }) => {
    if (!content) return null;

    return (
        <div className="ntb-markdown-content">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    // Code blocks and inline code
                    code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        const language = match ? match[1] : null;

                        if (!inline && (language || String(children).includes('\n'))) {
                            return (
                                <CodeBlock
                                    language={language}
                                    isDarkMode={isDarkMode}
                                >
                                    {children}
                                </CodeBlock>
                            );
                        }

                        return <InlineCode {...props}>{children}</InlineCode>;
                    },

                    // Tables
                    table({ children }) {
                        return (
                            <div className="ntb-table-wrapper">
                                <table className="ntb-markdown-table">{children}</table>
                            </div>
                        );
                    },

                    // Links - open in new tab
                    a({ href, children }) {
                        return (
                            <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ntb-markdown-link"
                            >
                                {children}
                            </a>
                        );
                    },

                    // Blockquotes
                    blockquote({ children }) {
                        return (
                            <blockquote className="ntb-markdown-blockquote">
                                {children}
                            </blockquote>
                        );
                    },

                    // Paragraphs - handle line breaks properly
                    p({ children }) {
                        return <p className="ntb-markdown-paragraph">{children}</p>;
                    },

                    // Headers
                    h1({ children }) {
                        return <h1 className="ntb-markdown-h1">{children}</h1>;
                    },
                    h2({ children }) {
                        return <h2 className="ntb-markdown-h2">{children}</h2>;
                    },
                    h3({ children }) {
                        return <h3 className="ntb-markdown-h3">{children}</h3>;
                    },
                    h4({ children }) {
                        return <h4 className="ntb-markdown-h4">{children}</h4>;
                    },

                    // Lists
                    ul({ children }) {
                        return <ul className="ntb-markdown-ul">{children}</ul>;
                    },
                    ol({ children }) {
                        return <ol className="ntb-markdown-ol">{children}</ol>;
                    },
                    li({ children }) {
                        return <li className="ntb-markdown-li">{children}</li>;
                    },

                    // Horizontal rule
                    hr() {
                        return <hr className="ntb-markdown-hr" />;
                    },

                    // Images
                    img({ src, alt }) {
                        return (
                            <img
                                src={src}
                                alt={alt || 'Image'}
                                className="ntb-markdown-img"
                                loading="lazy"
                            />
                        );
                    }
                }}
            >
                {content}
            </ReactMarkdown>
        </div>
    );
});

MarkdownRenderer.displayName = 'MarkdownRenderer';

export default MarkdownRenderer;
